import { Action } from 'redux';
export { isLoading, doneLoading } from '../config/actions';

export const GET_USER_CONTEXT = 'user/GET_USER_CONTEXT';
export interface GetUserContextAction extends Action<typeof GET_USER_CONTEXT> {
  readonly meta: ActionMetaData;
}
export const getUserContext = (meta: ActionMetaData = {}): GetUserContextAction => ({
  meta,
  type: GET_USER_CONTEXT,
});

export const PERSIST_USER_CONTEXT = 'user/PERSIST_USER_CONTEXT';
export interface PersistUserContextAction extends Action<typeof PERSIST_USER_CONTEXT> {
  readonly meta: ActionMetaData;
  readonly payload: {
    readonly context: UserContext;
  };
}

export const persistUserContext = (
  payload: PersistUserContextAction['payload'],
  meta: ActionMetaData = {},
): PersistUserContextAction => ({
  meta,
  payload,
  type: PERSIST_USER_CONTEXT,
});

// --- Action Types ---
export const LOGIN = 'user/LOGIN';
export interface LoginAction extends Action<typeof LOGIN> {
  readonly type: typeof LOGIN;
  readonly payload: {
    readonly username: string;
    readonly password: string;
  };
}

export const LOGIN_COMPLETE = 'user/LOGIN_COMPLETE';
export interface LoginCompleteAction extends Action<typeof LOGIN_COMPLETE> {
  readonly type: typeof LOGIN_COMPLETE;
  readonly payload: {
    readonly access_token: string;
    readonly expires_in: number;
    readonly refresh_token: string;
    readonly refresh_expires_in: number;
    readonly id_token: string;
    readonly timestamp: number;
  };
}

export interface LoginError {
  readonly message: string;
  readonly error_description?: string;
  readonly body?: any;
}
export const LOGIN_ERROR = 'user/LOGIN_ERROR';
export interface LoginErrorAction extends Action<typeof LOGIN_ERROR> {
  readonly type: typeof LOGIN_ERROR;
  readonly payload: LoginError;
}

export const LOGOUT = 'user/LOGOUT';
export interface LogoutAction extends Action<typeof LOGOUT> {
  readonly type: typeof LOGOUT;
  readonly payload?: {
    readonly redirectTo?: {
      readonly pathname: string;
      readonly hash?: string;
      readonly query?: {
        readonly redirect: string;
      };
    };
    readonly reason?: string;
  };
}

export const CLEAR_LOGIN_ERROR = 'user/CLEAR_LOGIN_ERROR';
export interface ClearLoginErrorAction extends Action<typeof CLEAR_LOGIN_ERROR> {
  readonly type: typeof CLEAR_LOGIN_ERROR;
}

export const REFRESHING_TOKEN = 'user/REFRESHING_TOKEN';
export interface RefreshTokenAction extends Action<typeof REFRESHING_TOKEN> {
  readonly type: typeof REFRESHING_TOKEN;
  readonly payload: {
    readonly refreshTokenPromise: Promise<any>;
  };
}

export const REFRESHING_TOKEN_COMPLETE = 'user/REFRESHING_TOKEN_COMPLETE';
export interface RefreshingTokenCompleteAction extends Action<typeof REFRESHING_TOKEN_COMPLETE> {
  readonly type: typeof REFRESHING_TOKEN_COMPLETE;
}

export const REFRESH_SESSION = 'user/REFRESH_SESSION';
export interface RefreshSessionAction extends Action<typeof REFRESH_SESSION> {
  readonly type: typeof REFRESH_SESSION;
}
export const SET_ADMIN_CONTEXT = 'user/SET_ADMIN_CONTEXT';
export const setAdminContext = (data: AdminContext) => {
  return {
    type: SET_ADMIN_CONTEXT,
    payload: data,
  };
};
export const SET_ADMIN_CONTEXT_COMPLETE = 'user/SET_ADMIN_CONTEXT_COMPLETE';
export interface SetAdminContextAction extends Action<typeof SET_ADMIN_CONTEXT_COMPLETE> {
  readonly payload: AdminContext;
}
export const setAdminContextComplete = (data: AdminContext) => {
  return {
    type: SET_ADMIN_CONTEXT_COMPLETE,
    payload: data,
  };
};

export const SET_ADMIN_SELECT_YN = 'user/SET_ADMIN_SELECT_YN';
export const setAdminSelected = (data: boolean) => {
  return {
    type: SET_ADMIN_SELECT_YN,
    payload: data,
  };
};

export const SET_ADMIN_SELECT_YN_COMPLETE = 'user/SET_ADMIN_SELECT_YN_COMPLETE';
export interface SetAdminSelectYNAction extends Action<typeof SET_ADMIN_SELECT_YN_COMPLETE> {
  readonly payload: boolean;
}
export const setAdminSelectedComplete = (data: boolean) => {
  return {
    type: SET_ADMIN_SELECT_YN_COMPLETE,
    payload: data,
  };
};
// superadmin reset Get
export const SUPER_ADMIN_GET_DATA = 'user/SUPER_ADMIN_GET_DATA';
export const getSuperadminData = (data: any) => ({
  type: SUPER_ADMIN_GET_DATA,
  payload: data,
});
export interface SetSuperAdminGetDataAction extends Action<typeof SUPER_ADMIN_GET_DATA> {
  readonly payload: any;
}
// superadmin reset Get end
export const RESET_PASSWORD = 'user/RESET_PASSWORD';
export const resetPassword = (data: any) => {
  return {
    type: RESET_PASSWORD,
    payload: data,
  };
};
export const FORGET_PASSWORD = 'user/FORGET_PASSWORD';
export const forgetPassword = (data: ForgetPasswordFormModel) => {
  return {
    type: FORGET_PASSWORD,
    payload: data,
  };
};
export const RESET_ADMIN_STATE = 'user/RESET_ADMIN_STATE';
export const resetAdminState = () => {
  return {
    type: RESET_ADMIN_STATE,
  };
};
export const SET_FORGET_PASSWORD_MESSAGE = 'user/SET_FORGET_PASSWORD_MESSAGE';
export const setForgetPasswordMessage = (data: string) => {
  return {
    type: SET_FORGET_PASSWORD_MESSAGE,
    payload: data,
  };
};
export interface SetForgetPasswordMessageAction extends Action<typeof SET_FORGET_PASSWORD_MESSAGE> {
  payload: string;
}

export interface ResetAdminStateAction extends Action<typeof RESET_ADMIN_STATE> {}

export const GET_FAVORITE = 'user/GET_FAVORITE';
export const getFavorite = () => {
  return {
    type: GET_FAVORITE,
  };
};
export const SET_FAVORITE = 'user/SET_FAVORITE';
export const setFavorite = (data: ReadonlyArray<FavoriteMenu>) => {
  return {
    type: SET_FAVORITE,
    payload: data,
  };
};
export interface SetFavoriteAction extends Action<typeof SET_FAVORITE> {
  readonly payload: ReadonlyArray<FavoriteMenu>;
}
export const REMOVE_FAVORITE = 'user/REMOVE_FAVORITE';
export const removeFavorite = (data: ReadonlyArray<FavoriteMenu>) => {
  return {
    type: REMOVE_FAVORITE,
    payload: data,
  };
};
export const ADD_FAVORITE = 'user/ADD_FAVORITE';
export const addFavorite = (data: FavoriteMenu) => ({
  type: ADD_FAVORITE,
  payload: data,
});

export type UserAction =
  | LoginAction
  | LoginCompleteAction
  | LoginErrorAction
  | LogoutAction
  | ClearLoginErrorAction
  | RefreshTokenAction
  | RefreshingTokenCompleteAction
  | RefreshSessionAction
  | PersistUserContextAction
  | GetUserContextAction
  | SetAdminContextAction
  | SetAdminSelectYNAction
  | ResetAdminStateAction
  | SetForgetPasswordMessageAction
  | SetFavoriteAction
  | SetSuperAdminGetDataAction;

// --- Actions ---

export const login = (credentials: LoginAction['payload']): LoginAction => ({
  type: LOGIN,
  payload: credentials,
});

export const loginComplete = (data: LoginCompleteAction['payload']): LoginCompleteAction => ({
  type: LOGIN_COMPLETE,
  payload: data,
});

export const loginFailed = (error: LoginError): LoginErrorAction => ({
  type: LOGIN_ERROR,
  payload: error,
});

export const logout = (data?: LogoutAction['payload']): LogoutAction => ({
  type: LOGOUT,
  payload: data,
});

export const clearLoginError = (): ClearLoginErrorAction => ({
  type: CLEAR_LOGIN_ERROR,
});

export const refreshingToken = (data: RefreshTokenAction['payload']): RefreshTokenAction => ({
  type: REFRESHING_TOKEN,
  payload: data,
});

export const refreshingTokenComplete = (): RefreshingTokenCompleteAction => ({
  type: REFRESHING_TOKEN_COMPLETE,
});

export const refreshSession = (): RefreshSessionAction => ({
  type: REFRESH_SESSION,
});
